import { useMutation } from "@apollo/client";
import {
  EditMessageInput,
  EditMessageMutation,
  EditMessageMutationVariables,
  MessageFileInput,
  MessageFragment,
} from "gql/graphql";
import { useCallback, useMemo } from "react";
import { getMessageFileInput } from "modules/utils/Functions";
import EDIT_MESSAGE_MUTATION from "modules/modals/networking/edit-message";
import useMessageThreadDraft from "./use-message-thread-draft";
import { useFile } from "modules/providers/file-provider";
import { useAuth } from "modules/providers/auth-provider";

const useEditMessage = (message: MessageFragment) => {
  const { me } = useAuth();
  const { draft, files } = useMessageThreadDraft({
    draftId: message.id,
  });
  const { removeLocalFiles } = useFile();

  const [editMessageAPI, { loading: isSending }] = useMutation<
    EditMessageMutation,
    EditMessageMutationVariables
  >(EDIT_MESSAGE_MUTATION);

  const editMessageInput = useMemo((): EditMessageInput | undefined => {
    if (!draft) {
      return undefined;
    }
    return {
      text: draft.text,
      quillDelta: draft.delta,
      gifList: draft.gifList,
      fileList: files?.map(
        (file): MessageFileInput => getMessageFileInput(file),
      ),
    };
  }, [draft, files]);

  const editMessage = useCallback(async () => {
    if (!editMessageInput || !me) {
      return;
    }

    // Decided that optimistic updates are not necessary for editing messages
    await editMessageAPI({
      variables: {
        id: message.id,
        input: editMessageInput,
      },
    });

    removeLocalFiles(files?.map((file) => file.id) || []);
  }, [editMessageAPI, editMessageInput, files, me, message, removeLocalFiles]);

  return {
    editMessage,
    isSending,
  };
};

export default useEditMessage;
